// Screen: "Clubs"
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClubs, createClub, getUserClub, getClubRankings } from '../apiClient';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const CLUB_COLORS = [
  { value: '#238636', label: 'Green' },
  { value: '#58a6ff', label: 'Blue' },
  { value: '#f0883e', label: 'Orange' },
  { value: '#a371f7', label: 'Purple' },
  { value: '#f85149', label: 'Red' },
  { value: '#d4af37', label: 'Gold' },
  { value: '#3fb950', label: 'Emerald' },
  { value: '#0ea5e9', label: 'Cyan' },
];

export default function ClubsPage() {
  const navigate = useNavigate();
  const { token, isClubMember } = useAuth();
  const isMobile = useIsMobile();
  const [clubs, setClubs] = useState([]);
  const [search, setSearch] = useState('');
  const [myClub, setMyClub] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', color: '#238636', membershipType: 'open' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewTab, setViewTab] = useState('browse');
  const [rankings, setRankings] = useState([]);

  const loadClubs = useCallback(async () => {
    const data = await getClubs(search || null);
    setClubs(data.clubs || []);
  }, [search]);

  useEffect(() => { loadClubs(); }, [loadClubs]);

  useEffect(() => {
    if (token) {
      getUserClub(token).then(data => setMyClub(data.club));
    }
  }, [token]);

  const handleCreate = async () => {
    setError('');
    if (!createForm.name.trim()) { setError('Club name is required'); return; }
    const result = await createClub(token, createForm.name, createForm.description, createForm.color, createForm.membershipType);
    if (result.success) {
      setSuccess('Club created!');
      setShowCreate(false);
      setCreateForm({ name: '', description: '', color: '#238636', membershipType: 'open' });
      loadClubs();
      getUserClub(token).then(data => setMyClub(data.club));
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to create club');
    }
  };

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700 }}>Clubs</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {!myClub && isClubMember && (
            <button onClick={() => setShowCreate(!showCreate)} style={{
              background: '#238636', color: '#fff', border: 'none',
              padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600
            }}>Create Club</button>
          )}
          {!myClub && !isClubMember && (
            <button onClick={() => navigate('/membership')} style={{
              background: '#21262d', color: '#8b949e', border: '1px solid #30363d',
              padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12
            }}>Join the Club to create</button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}
      {success && (
        <div style={{ background: '#1c3d2a', border: '1px solid #3fb950', color: '#3fb950', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{success}</div>
      )}

      {/* My Club Banner */}
      {myClub && (
        <div onClick={() => navigate(`/clubs/${myClub.clubId}`)} style={{
          background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
          border: `1px solid ${myClub.color || '#30363d'}`, marginBottom: 20, cursor: 'pointer'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: myClub.color || '#238636',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 700, color: '#fff'
            }}>
              {(myClub.name || '?')[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{myClub.name}</div>
              <div style={{ fontSize: 12, color: '#8b949e', textTransform: 'capitalize' }}>{myClub.role}</div>
            </div>
            <span style={{ color: '#58a6ff', fontSize: 12 }}>View Club &rarr;</span>
          </div>
        </div>
      )}

      {/* Create Club Form */}
      {showCreate && (
        <div style={{
          background: '#161b22', borderRadius: 12, padding: isMobile ? 20 : 28,
          border: '1px solid #30363d', marginBottom: 20
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Create a Club</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#c9d1d9', marginBottom: 6 }}>Club Name</label>
            <input value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
              maxLength={30} placeholder="Enter club name" style={{
                width: '100%', padding: '10px 12px', background: '#0d1117', border: '1px solid #30363d',
                borderRadius: 6, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', outline: 'none'
              }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#c9d1d9', marginBottom: 6 }}>Description</label>
            <textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
              maxLength={200} placeholder="What is your club about?" rows={3} style={{
                width: '100%', padding: '10px 12px', background: '#0d1117', border: '1px solid #30363d',
                borderRadius: 6, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', outline: 'none', resize: 'vertical'
              }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#c9d1d9', marginBottom: 6 }}>Color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CLUB_COLORS.map(c => (
                <button key={c.value} onClick={() => setCreateForm({ ...createForm, color: c.value })} style={{
                  width: 32, height: 32, borderRadius: 8, background: c.value, border: createForm.color === c.value ? '3px solid #fff' : '2px solid transparent',
                  cursor: 'pointer'
                }} title={c.label} />
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#c9d1d9', marginBottom: 6 }}>Membership</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[{ v: 'open', l: 'Open' }, { v: 'closed', l: 'Invite Only' }].map(m => (
                <button key={m.v} onClick={() => setCreateForm({ ...createForm, membershipType: m.v })} style={{
                  flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: createForm.membershipType === m.v ? '#238636' : '#21262d',
                  color: createForm.membershipType === m.v ? '#fff' : '#8b949e',
                  border: createForm.membershipType === m.v ? 'none' : '1px solid #30363d'
                }}>{m.l}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleCreate} style={{
              flex: 1, background: '#238636', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px', fontSize: 15, fontWeight: 700, cursor: 'pointer'
            }}>Create Club</button>
            <button onClick={() => setShowCreate(false)} style={{
              background: '#21262d', color: '#8b949e', border: '1px solid #30363d',
              borderRadius: 8, padding: '10px 16px', fontSize: 13, cursor: 'pointer'
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Browse / Rankings tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ key: 'browse', label: 'Browse Clubs' }, { key: 'rankings', label: 'Club Rankings' }].map(t => (
          <button key={t.key} onClick={() => {
            setViewTab(t.key);
            if (t.key === 'rankings' && rankings.length === 0) {
              getClubRankings().then(data => setRankings(Array.isArray(data) ? data : []));
            }
          }} style={{
            background: viewTab === t.key ? '#238636' : 'transparent',
            color: viewTab === t.key ? '#fff' : '#8b949e',
            border: viewTab === t.key ? 'none' : '1px solid #30363d',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {viewTab === 'browse' ? (
        <>
          {/* Search */}
          <div style={{ marginBottom: 16 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search clubs..." style={{
                width: '100%', padding: '10px 14px', background: '#161b22', border: '1px solid #30363d',
                borderRadius: 8, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', outline: 'none'
              }} />
          </div>

          {/* Club List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {clubs.length === 0 ? (
              <div style={{ background: '#161b22', borderRadius: 12, padding: 40, textAlign: 'center', border: '1px solid #30363d', color: '#484f58' }}>
                No clubs found. {!myClub ? 'Be the first to create one!' : ''}
              </div>
            ) : clubs.map(c => (
              <div key={c.club_id} onClick={() => navigate(`/clubs/${c.club_id}`)} style={{
                background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
                border: '1px solid #30363d', cursor: 'pointer', transition: 'border-color 0.2s'
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = c.color || '#58a6ff'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#30363d'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10, background: c.color || '#238636',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, fontWeight: 700, color: '#fff', flexShrink: 0
                  }}>
                    {(c.name || '?')[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#e2e8f0' }}>{c.name}</div>
                    {c.description && (
                      <div style={{ fontSize: 12, color: '#8b949e', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.description}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#c9d1d9' }}>{c.member_count}</div>
                    <div style={{ fontSize: 10, color: '#8b949e' }}>members</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        /* Club Rankings */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rankings.length === 0 ? (
            <div style={{ background: '#161b22', borderRadius: 12, padding: 40, textAlign: 'center', border: '1px solid #30363d', color: '#484f58' }}>
              No club rankings available yet.
            </div>
          ) : rankings.map((r, i) => (
            <div key={r.clubId} onClick={() => navigate(`/clubs/${r.clubId}`)} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
              background: i < 3 ? 'rgba(255,255,255,0.03)' : '#161b22',
              borderRadius: 10, border: '1px solid #30363d', cursor: 'pointer'
            }}>
              <span style={{
                width: 28, textAlign: 'center', fontSize: i < 3 ? 16 : 13,
                color: i === 0 ? '#d4af37' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#8b949e',
                fontWeight: i < 3 ? 700 : 400
              }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${r.rank}`}
              </span>
              <div style={{
                width: 36, height: 36, borderRadius: 8, background: r.color || '#238636',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0
              }}>
                {(r.name || '?')[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0' }}>{r.name}</div>
                <div style={{ fontSize: 11, color: '#8b949e' }}>{r.memberCount} members &middot; Avg {r.avgRP} RP</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#58a6ff' }}>{r.combinedRP}</div>
                <div style={{ fontSize: 10, color: '#8b949e' }}>total RP</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
